import { useState } from 'react';
import { useAppContext } from '~/App';
import { TextField } from '~/components/form/TextField';
import { Dialog } from '~/components/ui/Dialog';
import { Status, useCreateSpaceMutation } from '~/generated/graphql';
import { Button } from '../ui/buttons/Button';

const INDUSTRY_HINTS = {
  'Holiday Rentals & Property Management': {
    hint: 'Rooms or areas inside the property.',
    examples: [
      'Kitchen',
      'Bathroom',
      'Bedroom',
      'Garage',
      'Laundry',
      'Living Room',
      'Locked Cupboard',
      'Outdoor area',
    ],
  },
  'Laundry Services': {
    hint: 'Areas where your team operates from',
    examples: [
      'Bagging',
      'Bathroom',
      'Dispatch',
      'Laundry room',
      'Office',
      'Pressing',
      'Receiving',
      'Reception',
      'Storage room',
    ],
  },
  'Facilities Maintenance': {
    hint: 'Building areas you service.',
    examples: [
      'Amenities',
      'Reception',
      'Meeting room',
      'Office',
      'Server room',
      'Storeroom',
    ],
  },
  'Trades & Construction': {
    hint: 'Functional areas in your workshop or on site.',
    examples: ['Kitchen', 'Roof space', 'Plant area', 'Workshop'],
  },
  Industrial: {
    hint: 'Zones where equipment or machines are located.',
    examples: ['Production line', 'Welding bay', 'Tooling room'],
  },
  'Fleet & Transport': {
    hint: 'Functional areas in your depot or workshop.',
    examples: ['Workshop bay', 'Parts storage', 'Fleet yard'],
  },
  'Food & Beverage': {
    hint: 'Areas in your kitchen, venue, or service space.',
    examples: [
      'Kitchen',
      'Prep area',
      'Walk-in cooler',
      'Bar',
      'Dining area',
      'Wash area',
    ],
  },
  'Other Industry': {
    hint: 'Areas inside your shop, office, or workspace.',
    examples: ['Office', 'Back room', 'Storage area', 'Workshop'],
  },
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  onSpaceCreated: (spaceName: string) => void;
};

export function CreateSpaceDialog({
  isOpen,
  onClose,
  locationId,
  onSpaceCreated,
}: Props) {
  const [spaceName, setSpaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [, createSpace] = useCreateSpaceMutation();
  const { tenant } = useAppContext();

  const currentHint =
    tenant?.industry &&
    INDUSTRY_HINTS[tenant.industry as keyof typeof INDUSTRY_HINTS];

  // Create placeholder from 2-3 examples, prioritizing shorter ones
  const getPlaceholderExamples = () => {
    if (!currentHint || !currentHint.examples) return '';

    // Sort examples by length (shortest first) and take 2-3
    const sortedExamples = [...currentHint.examples].sort(
      (a, b) => a.length - b.length
    );
    const selectedExamples = sortedExamples.slice(0, 3);

    // If we have 3 examples and total length is reasonable, use all 3
    // Otherwise limit to 2 to keep placeholder readable
    const totalLength = selectedExamples.join(', ').length;
    const finalExamples =
      totalLength > 30 ? selectedExamples.slice(0, 2) : selectedExamples;

    return finalExamples.join(', ');
  };

  const placeholderText = currentHint
    ? `e.g. ${getPlaceholderExamples()}`
    : 'Enter space name...';

  const handleCreate = async () => {
    if (!spaceName.trim()) return;

    setIsCreating(true);
    try {
      await createSpace({
        locationId,
        input: {
          name: spaceName.trim(),
          images: [], // Empty array as required by SpaceInput
          status: Status.Active, // Required status field
        },
      });

      onSpaceCreated(spaceName.trim());
      handleClose();
    } catch (error) {
      console.error('Failed to create space:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setSpaceName('');
    setIsCreating(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && spaceName.trim()) {
      handleCreate();
    }
  };

  return (
    <Dialog
      show={isOpen}
      title='Create New Space'
      onClose={handleClose}
      isLoading={isCreating}
    >
      <div className='p-5'>
        <div className='mb-4'>
          <TextField
            name='spaceName'
            label='Space Name'
            value={spaceName}
            onChange={(e) => setSpaceName((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholderText}
            hint={currentHint ? currentHint.hint : undefined}
            required
            autoFocus
          />
        </div>

        <div className='flex justify-end gap-3'>
          <Button
            variant='secondary'
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            data-usetiful='create-space'
            onClick={handleCreate}
            disabled={!spaceName.trim() || isCreating}
            loading={isCreating}
          >
            Create Space
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
